/** Minimal typing of the Cloudflare Turnstile browser API that the demo uses. */
interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      appearance?: 'always' | 'execute' | 'interaction-only';
    },
  ): string;
  reset(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile did not initialise'));
    };
    script.onerror = () => {
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.append(script);
  });
}

/** Resolves with a fresh token; the widget is invisible unless Cloudflare needs interaction. */
export async function getTurnstileToken(container: HTMLElement, siteKey: string): Promise<string> {
  const turnstile = await loadScript();
  return new Promise((resolve, reject) => {
    container.replaceChildren();
    turnstile.render(container, {
      sitekey: siteKey,
      appearance: 'interaction-only',
      callback: resolve,
      'error-callback': () => {
        reject(new Error('Turnstile check failed'));
      },
    });
  });
}
